import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  FaSyncAlt,
  FaUserClock,
  FaPlus,
  FaUsers,
  FaSpinner,
  FaPen,
} from "react-icons/fa";
import MdlEditPayRollStaffDetails from "../dialog/MdlEditPayRollStaffDetails";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const PAGE_TITLE = "Add Salary";

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const ui = {
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  border: "rgba(148, 163, 184, 0.28)",
};

const SALARY_TYPE_PALETTE = {
  Monthly: { bg: "rgba(99, 102, 241, 0.12)", color: "#4338ca" },
  Daily: { bg: "rgba(16, 185, 129, 0.14)", color: "#047857" },
  Hourly: { bg: "rgba(245, 158, 11, 0.16)", color: "#b45309" },
  default: { bg: "rgba(148, 163, 184, 0.18)", color: "#334155" },
};

function chipForSalaryType(value) {
  if (!value) return SALARY_TYPE_PALETTE.default;
  const label =
    String(value).charAt(0).toUpperCase() + String(value).slice(1).toLowerCase();
  return SALARY_TYPE_PALETTE[label] || SALARY_TYPE_PALETTE.default;
}

function formatYearMonth(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length >= 2) {
    const y = parts[0];
    const m = parseInt(parts[1], 10);
    if (m >= 1 && m <= 12) return `${MONTH_SHORT[m - 1]} ${y}`;
  }
  return String(value);
}

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatDateDMY(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString();
  return String(value);
}

function formatWorkingHour(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}h`;
}

/** Last calendar day of selected month (YYYY-MM). */
function getMonthPeriodEnd(selectedMonth) {
  if (!selectedMonth) return null;
  const parts = String(selectedMonth).split("-");
  if (parts.length < 2) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return new Date(year, month, 0);
}

function parseJoiningDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3], 10));
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Same rule as payroll seed: joining on or before last day of month. */
function getJoiningEligibility(joiningDate, selectedMonth) {
  const periodEnd = getMonthPeriodEnd(selectedMonth);
  if (!periodEnd) {
    return { canAdd: false, reason: "Select a payroll month." };
  }
  const joined = parseJoiningDate(joiningDate);
  if (!joined) {
    return { canAdd: true, reason: "" };
  }
  const joinedDay = new Date(joined.getFullYear(), joined.getMonth(), joined.getDate());
  const lastDay = new Date(
    periodEnd.getFullYear(),
    periodEnd.getMonth(),
    periodEnd.getDate()
  );
  if (joinedDay > lastDay) {
    return {
      canAdd: false,
      reason: `Joining ${formatDateDMY(joiningDate)} is after ${formatYearMonth(selectedMonth)}.`,
    };
  }
  return { canAdd: true, reason: "" };
}

function NotSeededStaffRow({ row, eligibility, onAdd, onEdit, isAdding, busy }) {
  const initial = (row.staff_name || "?").charAt(0).toUpperCase();
  const chip = chipForSalaryType(row.salary_type);
  const canAdd = eligibility.canAdd && !busy;
  return (
    <tr
      className={`pp-add-salary-row${eligibility.canAdd ? "" : " pp-add-salary-row--ineligible"}`}
    >
      <td>
        <div className="d-flex align-items-center gap-3 min-w-0">
          <span className="pp-add-salary-avatar pp-add-salary-avatar--muted" aria-hidden>
            {initial}
          </span>
          <div className="min-w-0">
            <div className="pp-add-salary-name text-truncate">{row.staff_name}</div>
            <div className="pp-add-salary-meta text-truncate">
              {row.designation || row.staff_role || row.department_name || "—"}
            </div>
          </div>
        </div>
      </td>
      <td>
        <span
          className="pp-add-salary-type-chip"
          style={{ background: chip.bg, color: chip.color }}
        >
          {row.salary_type || "—"}
        </span>
      </td>
      <td className="text-end pp-add-salary-amount">{formatINR(row.salary)}</td>
      <td className="text-nowrap pp-add-salary-cell-muted">
        {formatWorkingHour(row.working_hour)}
      </td>
      <td className="text-nowrap pp-add-salary-cell-muted">
        <span
          className={
            eligibility.canAdd
              ? "pp-add-salary-join-ok"
              : "pp-add-salary-join-warn"
          }
          title={eligibility.reason || undefined}
        >
          {formatDateDMY(row.joining_date)}
        </span>
      </td>
      <td className="pp-add-salary-cell-muted">{row.week_off || "—"}</td>
      <td className="text-end">
        <div className="pp-add-salary-actions">
          <button
            type="button"
            className="pp-add-salary-edit-btn"
            onClick={() => onEdit(row)}
            disabled={busy}
            title="Edit payroll details (salary, hours, joining date, week off)"
          >
            <FaPen aria-hidden />
            <span className="pp-add-salary-edit-btn__label">Edit</span>
          </button>
          {eligibility.canAdd ? (
            <button
              type="button"
              className={`pp-add-salary-row-btn${isAdding ? " pp-add-salary-row-btn--loading" : ""}`}
              onClick={() => onAdd(row)}
              disabled={!canAdd}
              title={eligibility.reason || "Add monthly salary row"}
              aria-busy={isAdding || undefined}
            >
              <span className="pp-add-salary-row-btn__icon" aria-hidden>
                {isAdding ? (
                  <FaSpinner className="pp-add-salary-row-btn__spin" />
                ) : (
                  <FaPlus />
                )}
              </span>
              <span className="pp-add-salary-row-btn__label">
                {isAdding ? "Adding…" : "Add Salary"}
              </span>
            </button>
          ) : (
            <span
              className="pp-add-salary-ineligible-hint"
              title={eligibility.reason}
            >
              Not eligible
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

const PayRollAddSalary = () => {
  const loggedIn = localStorage.getItem("token") !== null;

  const [mSessionList, setMSessionList] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [payrollStaffAll, setPayrollStaffAll] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [monthJustSeeded, setMonthJustSeeded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seedingStaffId, setSeedingStaffId] = useState(null);
  const [editStaffRecord, setEditStaffRecord] = useState(null);
  const [loadError, setLoadError] = useState("");
  const sessionReqIdRef = useRef(0);
  const dataReqIdRef = useRef(0);

  const fetchSessionList = useCallback(async () => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getSessionList.php`;
    const reqId = ++sessionReqIdRef.current;

    try {
      const response = await axios.get(URL, { params: { school_code: SCHOOL_CODE } });
      if (reqId !== sessionReqIdRef.current) return;
      if (response.data?.api_status !== "OK") return;

      const sessions = response.data.data || [];
      setMSessionList(sessions);
      if (sessions.length === 0) return;

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const chosen =
        sessions.find(
          (s) =>
            s.status === "Active" ||
            (s.session_name && s.session_name.includes(String(currentYear)))
        ) || sessions[0];

      setSelectedSessionId(chosen.session_id);

      if (chosen.start_date && chosen.end_date) {
        const startDate = new Date(chosen.start_date);
        const endDate = new Date(chosen.end_date);
        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
          const sy = startDate.getFullYear();
          const ey = endDate.getFullYear();
          const sm = startDate.getMonth() + 1;
          const em = endDate.getMonth() + 1;
          const inRange =
            sy <= currentYear &&
            ey >= currentYear &&
            currentMonth >= sm &&
            currentMonth <= em;
          setSelectedMonth(
            inRange
              ? `${currentYear}-${String(currentMonth).padStart(2, "0")}`
              : `${sy}-${String(sm).padStart(2, "0")}`
          );
        }
      }
    } catch (err) {
      if (reqId !== sessionReqIdRef.current) return;
      console.error("Error fetching session list:", err);
    }
  }, []);

  const fetchMonthData = useCallback(async () => {
    if (!selectedMonth || !selectedSessionId) return;

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const [year, month] = selectedMonth.split("-");
    const reqId = ++dataReqIdRef.current;

    setLoading(true);
    setLoadError("");

    try {
      const [monthlyRes, staffRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollMonthlySalary.php`,
          {
            params: {
              school_code: SCHOOL_CODE,
              session_id: selectedSessionId,
              month: parseInt(month, 10),
              year: parseInt(year, 10),
            },
          }
        ),
        axios.get(
          `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollStaffList.php`,
          {
            params: {
              school_code: SCHOOL_CODE,
              session_id: selectedSessionId,
            },
          }
        ),
      ]);

      if (reqId !== dataReqIdRef.current) return;

      let rows = [];
      let seededFlag = false;
      const errors = [];

      if (monthlyRes.data?.api_status === "OK") {
        rows = monthlyRes.data.staff_payroll || [];
        seededFlag = Boolean(monthlyRes.data.seeded);
      } else {
        errors.push(
          monthlyRes.data?.api_remark || "Could not load monthly payroll."
        );
      }

      let allStaff = [];
      if (staffRes.data?.api_status === "OK") {
        allStaff = staffRes.data.data || [];
      } else {
        errors.push(
          staffRes.data?.api_remark || "Could not load payroll staff list."
        );
      }

      if (errors.length) setLoadError(errors.join(" "));

      setMonthlyRows(rows);
      setMonthJustSeeded(seededFlag);
      setPayrollStaffAll(allStaff);
    } catch (err) {
      if (reqId !== dataReqIdRef.current) return;
      console.error("Error loading add-salary data:", err);
      setLoadError("Failed to load staff lists. Please try again.");
      setMonthlyRows([]);
      setPayrollStaffAll([]);
    } finally {
      if (reqId !== dataReqIdRef.current) return;
      setLoading(false);
    }
  }, [selectedMonth, selectedSessionId]);

  useEffect(() => {
    fetchSessionList();
  }, [fetchSessionList]);

  useEffect(() => {
    if (!selectedMonth || !selectedSessionId) return;
    fetchMonthData();
  }, [selectedMonth, selectedSessionId, fetchMonthData]);

  const monthOptions = useMemo(() => {
    if (!selectedSessionId || mSessionList.length === 0) return [];
    const session = mSessionList.find((s) => s.session_id === selectedSessionId);
    if (!session?.start_date || !session?.end_date) return [];
    const startDate = new Date(session.start_date);
    const endDate = new Date(session.end_date);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return [];
    }
    const months = [];
    const cur = new Date(startDate);
    while (cur <= endDate) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      const value = `${y}-${String(m + 1).padStart(2, "0")}`;
      months.push({
        value,
        label: `${cur.toLocaleString("default", { month: "long" })} ${y}`,
        key: value,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  }, [selectedSessionId, mSessionList]);

  const seededStaffIds = useMemo(() => {
    const ids = new Set();
    for (const row of monthlyRows) {
      if (row.staff_id != null) ids.add(String(row.staff_id));
    }
    return ids;
  }, [monthlyRows]);

  const notSeededStaff = useMemo(() => {
    return payrollStaffAll.filter(
      (s) => s.staff_id != null && !seededStaffIds.has(String(s.staff_id))
    );
  }, [payrollStaffAll, seededStaffIds]);

  const notSeededWithEligibility = useMemo(() => {
    return notSeededStaff.map((row) => ({
      row,
      eligibility: getJoiningEligibility(row.joining_date, selectedMonth),
    }));
  }, [notSeededStaff, selectedMonth]);

  const eligibleNotSeededCount = useMemo(
    () => notSeededWithEligibility.filter((x) => x.eligibility.canAdd).length,
    [notSeededWithEligibility]
  );

  const handleSessionChange = useCallback(
    (e) => {
      const id = e.target.value;
      const sess = mSessionList.find((s) => s.session_id === id);
      if (!sess) return;
      setSelectedSessionId(id);
      if (sess.start_date) {
        const d = new Date(sess.start_date);
        if (!Number.isNaN(d.getTime())) {
          setSelectedMonth(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
          );
        }
      }
    },
    [mSessionList]
  );

  const handleMonthChange = useCallback((e) => {
    setSelectedMonth(e.target.value);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchMonthData();
  }, [fetchMonthData]);

  const handleAddSalaryForStaff = useCallback(
    async (row) => {
      if (!selectedMonth || !selectedSessionId || !row?.staff_id) return;

      const eligibility = getJoiningEligibility(row.joining_date, selectedMonth);
      if (!eligibility.canAdd) {
        myToast.notify_warning(eligibility.reason);
        return;
      }

      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const [year, month] = selectedMonth.split("-");
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollMonthlySalary.php`;
      const staffKey = String(row.staff_id);

      setSeedingStaffId(staffKey);
      setLoadError("");

      try {
        const response = await axios.get(URL, {
          params: {
            school_code: SCHOOL_CODE,
            session_id: selectedSessionId,
            month: parseInt(month, 10),
            year: parseInt(year, 10),
            seed_missing: "1",
            staff_id: staffKey,
          },
        });

        if (response.data?.api_status === "OK") {
          if (response.data.seeded) {
            myToast.notify_success(
              `Salary added for ${row.staff_name || "staff"} (${formatYearMonth(selectedMonth)}).`
            );
            await fetchMonthData();
          } else {
            myToast.notify_info(
              "Salary row already exists or could not be created. Refreshing list."
            );
            await fetchMonthData();
          }
        } else {
          const msg = response.data?.api_remark || "Could not add salary.";
          myToast.notify_error(msg);
        }
      } catch (err) {
        console.error("Error adding salary:", err);
        myToast.notify_error("Failed to add salary. Please try again.");
      } finally {
        setSeedingStaffId(null);
      }
    },
    [selectedMonth, selectedSessionId, fetchMonthData]
  );

  const handleOpenEditStaff = useCallback((row) => {
    setEditStaffRecord(row);
  }, []);

  const handleCloseEditStaff = useCallback(() => {
    setEditStaffRecord(null);
  }, []);

  const handleEditStaffSaved = useCallback(async () => {
    await fetchMonthData();
  }, [fetchMonthData]);

  const rowActionBusy = loading || seedingStaffId != null;

  if (!loggedIn) {
    return (
      <div className="container mt-5 text-center">
        <h2>Access Denied</h2>
        <p>You must be logged in to view this page.</p>
        <Link to="/Login" className="btn btn-primary">
          Go to Login
        </Link>
      </div>
    );
  }

  const monthLabel = selectedMonth ? formatYearMonth(selectedMonth) : "—";

  return (
    <div
      className="content payroll-page"
      style={{ background: COLORS.white, fontFamily: fontStack, color: ui.text }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <Link to="/AdminMain/PayRoll" style={{ color: COLORS.white }}>
                {PAGE_TITLE}
              </Link>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp payroll-add-salary-shell"
        style={{
          "--mMargin": "0",
          padding: 0,
          overflow: "hidden",
          borderRadius: 14,
        }}
      >
        <div
          className="header-section"
          style={{
            background: HEADER_GRADIENT,
            color: "#fff",
            padding: "18px 20px",
            margin: 0,
            borderRadius: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            minHeight: 88,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "999px",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="fa fa-money" style={{ fontSize: 22 }} aria-hidden />
            </div>
            <div className="min-w-0">
              <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>
                {PAGE_TITLE}
              </h5>
              <small
                style={{
                  display: "block",
                  marginTop: 6,
                  fontSize: 14,
                  lineHeight: 1.4,
                  opacity: 0.92,
                }}
              >
                Staff not added for {monthLabel}
              </small>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <select
              value={selectedSessionId}
              onChange={handleSessionChange}
              className="pp-pill-select pp-pill-select--session"
              aria-label="Academic session"
            >
              {mSessionList.length === 0 ? (
                <option value="">Loading sessions…</option>
              ) : (
                mSessionList.map((s) => (
                  <option key={s.session_id} value={s.session_id}>
                    {s.session_name}
                    {s.status ? ` (${s.status})` : ""}
                  </option>
                ))
              )}
            </select>
            <select
              value={selectedMonth}
              onChange={handleMonthChange}
              disabled={monthOptions.length === 0}
              className="pp-pill-select"
              aria-label="Payroll month"
              style={{ minWidth: 150 }}
            >
              {monthOptions.length > 0 ? (
                monthOptions.map((m) => (
                  <option key={m.key} value={m.value}>
                    {m.label}
                  </option>
                ))
              ) : (
                <option value="">No months</option>
              )}
            </select>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading || !selectedSessionId}
              className={`pp-icon-btn ${loading ? "spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt />
            </button>
          </div>
        </div>

        <div style={{ padding: "12px 16px 18px" }}>
          <div className="pp-add-salary-action-bar">
            <div className="pp-add-salary-stat">
              <span className="pp-add-salary-stat__icon" aria-hidden>
                <FaUsers />
              </span>
              <div>
                <div className="pp-add-salary-stat__value">{eligibleNotSeededCount}</div>
                <div className="pp-add-salary-stat__label">
                  can add for {monthLabel}
                  {notSeededStaff.length !== eligibleNotSeededCount
                    ? ` (${notSeededStaff.length} pending total)`
                    : ""}
                </div>
              </div>
            </div>
            <p className="pp-add-salary-action-hint mb-0">
              Joining date must be on or before the last day of the selected month.
            </p>
          </div>

          {monthJustSeeded ? (
            <div className="pp-add-salary-toolbar pp-add-salary-toolbar--banner-only">
              <span className="pp-add-salary-seed-banner">
                Salary rows were auto-added on load ({monthLabel}).
              </span>
            </div>
          ) : null}

          {loadError ? (
            <div className="pp-add-salary-alert" role="alert">
              {loadError}
            </div>
          ) : null}

          {loading ? (
            <div className="pp-add-salary-loading">Loading staff lists…</div>
          ) : (
            <div className="pp-add-salary-lists">
              <section className="pp-add-salary-panel pp-add-salary-panel--pending">
                <header className="pp-add-salary-panel__head">
                  <span className="pp-add-salary-panel__icon pp-add-salary-panel__icon--pending">
                    <FaUserClock />
                  </span>
                  <div className="min-w-0 flex-grow-1">
                    <h2 className="pp-add-salary-panel__title">Not added</h2>
                    <p className="pp-add-salary-panel__count">
                      {notSeededStaff.length} staff without a salary row
                    </p>
                  </div>
                </header>
                <div className="pp-add-salary-panel__body">
                  {notSeededStaff.length > 0 ? (
                    <div className="table-responsive pp-add-salary-table-wrap">
                      <table className="table align-middle mb-0 pp-table pp-add-salary-table pp-add-salary-table--modern">
                        <thead>
                          <tr>
                            <th>Staff</th>
                            <th>Type</th>
                            <th className="text-end">Basic</th>
                            <th>Hours</th>
                            <th>Joining</th>
                            <th>Week off</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notSeededWithEligibility.map(({ row, eligibility }, idx) => (
                            <NotSeededStaffRow
                              key={`${row.staff_id}-${idx}`}
                              row={row}
                              eligibility={eligibility}
                              onAdd={handleAddSalaryForStaff}
                              onEdit={handleOpenEditStaff}
                              isAdding={seedingStaffId === String(row.staff_id)}
                              busy={rowActionBusy}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="pp-add-salary-empty">
                      <p className="pp-add-salary-empty__title">All staff added</p>
                      <p className="pp-add-salary-empty__hint">
                        Every active payroll staff member has a salary row for this month.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>

      {editStaffRecord ? (
        <MdlEditPayRollStaffDetails
          show={Boolean(editStaffRecord)}
          actionClose={handleCloseEditStaff}
          staffRecord={editStaffRecord}
          sessionId={selectedSessionId}
          onSaved={handleEditStaffSaved}
        />
      ) : null}
    </div>
  );
};

export default PayRollAddSalary;
